import React, { useState, useEffect, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import axios from "axios";
import config from "../../config.js";
import Update_Icon from "../../images/table/Update_icon.svg";
import Track_Icon from "../../images/table/View_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import DynamicTable from "../common/DynamicTable";
import DeleteModal from "../common/DeleteModal";
import { usePermission } from "../../hooks/usePermission";
import { useUser } from "../../pages/login/UserContextPersistent";
import { useTranslation } from "react-i18next";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { useUserInfo } from "../../hooks/useUserInfo";

const isRouteEyeAdmin = () => {
  const userState = localStorage.getItem("userState");
  if (!userState) return false;
  try {
    const user = JSON.parse(userState).user;
    return user.organizationId === 5 && user.email === "sales@routeeye.io";
  } catch {
    return false;
  }
};

const getAdminHeaders = () => [
  { key: "Stop ID", value: "Stop ID" },
  { key: "Stop Name", value: "Stops" },
  { key: "organizationName", value: "Organization" },
  { key: "siteID", value: "Site" },
  { key: "Routes", value: "Routes" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

const getRegularHeaders = () => [
  { key: "Stop ID", value: "Stop ID" },
  { key: "Stop Name", value: "Stops" },
  { key: "Routes", value: "Routes" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

const customActions = [
  {
    id: "update",
    label: "Update",
    icon: Update_Icon,
    path: "/stops/edit",
    requiredPermission: { path: "/api/stop", action: "PUT" },
  },
  {
    id: "view",
    label: "Monitor",
    icon: Track_Icon,
    path: "/stops/view",
    requiredPermission: { path: "/api/stop/id", action: "GET" },
  },
  {
    id: "delete",
    label: "Delete",
    className: "delete",
    icon: Delete_Icon,
    path: "",
    requiredPermission: { path: "/api/stop", action: "DELETE" },
  },
];

export default function Stops() {
  const { t } = useTranslation();
  const [stopsData, setStopsData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    route: "",
    stop: "",
    status: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedStopId, setSelectedStopId] = useState(null);
  const [selectedStopData, setSelectedStopData] = useState(null);
  const { state } = useUser();
  const { userId } = useUserInfo();

  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);

  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const dynamicHeaders = isRouteEyeAdmin()
    ? getAdminHeaders()
    : getRegularHeaders();

  // Enhanced permission check function
  const checkPermission = (requiredPath, requiredAction = null) => {
    if (!state?.features || !requiredPath) {
      return true;
    }

    return state.features.some((feature) => {
      const pathMatches = feature.api_path.includes(requiredPath);
      return requiredAction
        ? pathMatches && feature.action === requiredAction
        : pathMatches;
    });
  };

  // Filter actions based on permissions
  const getPermittedActions = () => {
    return customActions.filter((action) => {
      if (!action.requiredPermission) return true;
      const { path, action: requiredAction } = action.requiredPermission;
      return checkPermission(path, requiredAction);
    });
  };

  // Define your filters configuration
  // const [filterConfig, setFilterConfig] = useState([
  //   {
  //     id: "route",
  //     label: "routes",
  //     helperText: "Select Route",
  //     options: [],
  //   },
  //   {
  //     id: "stop",
  //     label: "stops",
  //     helperText: "Select Stop",
  //     options: [],
  //   },
  // ]);
  const [filterConfig, setFilterConfig] = useState([]);

  useEffect(() => {
    if (isRouteEyeAdmin()) {
      // Admin filters: Organization, Site, Routes, Stop
      setFilterConfig([
        {
          id: "organization",
          label: "Organization",
          helperText: "Select organization",
          options: organizationOptions,
        },
        {
          id: "site",
          label: "Site",
          helperText: "Select site",
          options: siteOptions,
        },
        {
          id: "route",
          label: "routes",
          helperText: "Select Route",
          options: [],
        },
        {
          id: "stop",
          label: "stops",
          helperText: "Select Stop",
          options: [],
        },
      ]);
    } else {
      // Regular user filters: Route, Stop
      setFilterConfig([
        {
          id: "route",
          label: "routes",
          helperText: "Select Route",
          options: [],
        },
        {
          id: "stop",
          label: "stops",
          helperText: "Select Stop",
          options: [],
        },
      ]);
    }
  }, [organizationOptions, siteOptions]);

  const fetchOrganizationsAndSites = async () => {
    try {
      // Fetch organizations
      const orgResponse = await axios.get(`${config.baseURL}/organization`, {
        withCredentials: true,
      });

      let organizations = [];
      if (Array.isArray(orgResponse.data)) {
        organizations = orgResponse.data;
      } else if (Array.isArray(orgResponse.data.organizations)) {
        organizations = orgResponse.data.organizations;
      }

      const orgOptions = organizations.map((org) => ({
        value: org.id.toString(),
        label: org.name || `Organization ${org.id}`,
      }));
      setOrganizationOptions(orgOptions);

      // Fetch sites
      const siteResponse = await axios.get(`${config.baseURL}/site`, {
        withCredentials: true,
      });

      let sites = [];
      if (Array.isArray(siteResponse.data)) {
        sites = siteResponse.data;
      } else if (Array.isArray(siteResponse.data.sites)) {
        sites = siteResponse.data.sites;
      }

      const siteOptions = sites.map((site) => ({
        value: site.id.toString(),
        label: site.siteId || site.name || `Site ${site.id}`,
      }));
      setSiteOptions(siteOptions);
    } catch (error) {
      console.error("Error fetching organizations/sites:", error);
    }
  };

  // Function to fetch routes by coordinates
  const getRouteByStopCoordinates = async (lat, lng, orgId) => {
    try {
      const response = await axios.get(`${config.baseURL}/route/coordinates/`, {
        params: {
          longitude: lng,
          latitude: lat,
          organizationId: orgId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching routes by coordinates:", error);
      return [];
    }
  };

  // Fetch stops data from API on component mount
  useEffect(() => {
    if (!checkPermission("/api/stop/organization", "GET")) {
      console.log("No permission to view stops");
      navigate("/dashboard");
      return;
    }

    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        if (isRouteEyeAdmin()) {
          await fetchOrganizationsAndSites();
        }
        const stopsResponse = await axios.get(
          isRouteEyeAdmin()
            ? `${config.baseURL}/stop` // Admin endpoint - all stops
            : `${config.baseURL}/stop/organization`, // Regular user endpoint
          { withCredentials: true }
        );
        const stops = stopsResponse.data;

        // Fetch routes for each stop
        const stopsWithRoutes = await Promise.all(
          stops.map(async (stop) => {
            const routes = await getRouteByStopCoordinates(
              stop.latitude,
              stop.longitude,
              stop.organizationId
            );
            console.log("routes", routes);
            return { ...stop, routes };
          })
        );

        console.log(stopsWithRoutes);

        setStopsData(stopsWithRoutes);

        // Extract unique routes for filter
        const uniqueRoutes = [];
        const routeIds = new Set();

        // Extract unique stops for filter
        const uniqueStops = [];
        const stopIds = new Set();

        stopsWithRoutes.forEach((stop) => {
          // Process routes
          if (stop.routes && stop.routes.length > 0) {
            stop.routes.forEach((route) => {
              const routeId = route.id || route.Id || route.ID;
              const routeName = route.name || route.Name || `Route ${routeId}`;

              if (routeId && !routeIds.has(routeId)) {
                routeIds.add(routeId);
                uniqueRoutes.push({
                  value: routeId.toString(),
                  label: routeName,
                });
              }
            });
          }

          // Process stops
          const stopId = stop.id || stop.Id || stop.ID;
          const stopName = stop.name || stop.Name || `Stop ${stopId}`;

          if (stopId && !stopIds.has(stopId)) {
            stopIds.add(stopId);
            uniqueStops.push({
              value: stopId.toString(),
              label: stopName,
            });
          }
        });

        // Update filter config with dynamic routes and stops
        setFilterConfig((prev) => {
          const updated = [...prev];

          // Update routes filter
          const routeIndex = updated.findIndex((f) => f.id === "route");
          if (routeIndex >= 0) {
            updated[routeIndex].options = uniqueRoutes;
          }

          // Update stops filter
          const stopIndex = updated.findIndex((f) => f.id === "stop");
          if (stopIndex >= 0) {
            updated[stopIndex].options = uniqueStops;
          }

          return updated;
        });

        setIsLoading(false);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.log("Unauthorized request");
          navigate("/");
          return;
        }
        console.error("Error fetching initial data:", error.response);
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [navigate]);

  const formatDataForTable = (data) => {
    return data.map((stop) => {
      let routesDisplay = "No Route";

      if (stop.routes && stop.routes.length > 0) {
        routesDisplay = stop.routes
          .map((route) => route.name || `Route ${route.id}`)
          .join(", ");
      }

      return {
        "Stop ID": stop.stopId || "N/A",
        "Stop Name": stop.name || "N/A",
        organizationName: stop.organization?.name || "N/A",
        siteID: stop.site?.siteId || "N/A",
        Routes: routesDisplay,
        id: stop.id,
        originalData: stop,
      };
    });
  };

  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  // Handle filter changes
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };

  // Filter data based on search query and filters
  const filteredStopsData = stopsData.filter((stop) => {
    // Admin filters
    if (isRouteEyeAdmin()) {
      const organizationMatch =
        !filters.organization ||
        (stop.organization?.id &&
          String(stop.organization.id) === String(filters.organization));

      const siteMatch =
        !filters.site ||
        (stop.site?.id && String(stop.site.id) === String(filters.site));

      const routeMatch =
        !filters.route ||
        (stop.routes &&
          stop.routes.some(
            (route) => String(route.id) === String(filters.route)
          ));

      const stopId = stop.id || stop.Id || stop.ID;
      const stopMatch =
        !filters.stop || String(stopId) === String(filters.stop);

      if (!organizationMatch || !siteMatch || !routeMatch || !stopMatch) {
        return false;
      }
    } else {
      // Regular user filters
      const routeMatch =
        !filters.route ||
        (stop.routes &&
          stop.routes.some(
            (route) => String(route.id) === String(filters.route)
          ));

      const stopId = stop.id || stop.Id || stop.ID;
      const stopMatch =
        !filters.stop || String(stopId) === String(filters.stop);

      const statusMatch = !filters.status || stop.status === filters.status;

      if (!routeMatch || !stopMatch || !statusMatch) {
        return false;
      }
    }

    // Search query (same for both)
    if (!searchQuery || searchQuery.trim() === "") {
      return true;
    }

    const query = searchQuery.toLowerCase().trim();

    const stopIdMatch =
      stop.stopId && stop.stopId.toLowerCase().includes(query);

    const stopNameMatch = stop.name && stop.name.toLowerCase().includes(query);

    const routeSearchMatch =
      stop.routes &&
      stop.routes.some(
        (route) => route.name && route.name.toLowerCase().includes(query)
      );

    const organizationMatch =
      stop.organization?.name &&
      stop.organization.name.toLowerCase().includes(query);

    const siteMatch =
      stop.site?.siteId && stop.site.siteId.toLowerCase().includes(query);

    return (
      stopIdMatch ||
      stopNameMatch ||
      routeSearchMatch ||
      organizationMatch ||
      siteMatch
    );
  });

  console.log("Filtered stops data:", filteredStopsData);

  // Handle view all stops on map
  const handleViewAllStopsOnMap = () => {
    if (checkPermission("/api/stop/organization", "GET")) {
      navigate("/stop/all/map", {
        state: { allStopsData: stopsData },
      });
    } else {
      console.log("No permission to view all stops on map");
    }
  };

  // Add stop handler
  const handleAddStop = () => {
    if (checkPermission("/api/stop", "POST")) {
      navigate("/stop/add");
    } else {
      console.log("No permission to add stop");
    }
  };

  // Handle delete dialog open
  const openDeleteModal = (stopId, stopData) => {
    if (checkPermission("/api/stop", "DELETE")) {
      setSelectedStopId(stopId);
      setSelectedStopData(stopData);
      setIsDeleteModalOpen(true);
    } else {
      console.log("No permission to delete stops");
    }
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedStopId(null);
    setSelectedStopData(null);
  };

  // Handle delete action
  const handleDelete = async () => {
    if (selectedStopId && checkPermission("/api/stop", "DELETE")) {
      try {
        const userState = localStorage.getItem("userState");
        const parsedUserState = JSON.parse(userState);
        const orgId = parsedUserState.user.organizationId;

        await axios.delete(`${config.baseURL}/stop`, {
          data: {
            id: selectedStopId,
            organizationId: orgId,
            deletedBy: userId,
          },
          withCredentials: true,
        });

        // Update local state
        const updatedStops = stopsData.filter(
          (stop) => stop.id !== selectedStopId
        );
        setStopsData(updatedStops);
        handleDeleteDialogClose();
      } catch (error) {
        console.error("Error deleting stop:", error);
        handleDeleteDialogClose();
      }
    }
  };

  // View stop details
  const handleViewStopDetails = (stopData) => {
    if (checkPermission("/api/stop/id", "GET")) {
      navigate("/stop/track", {
        state: {
          selectedStopData: stopData,
          localStopsData: stopsData,
        },
      });
    } else {
      console.log("No permission to view stop details");
    }
  };

  // Update stop details
  const handleUpdateStop = (stopId) => {
    if (checkPermission("/api/stop", "PUT")) {
      navigate(`/stop/${stopId}`);
    } else {
      console.log("No permission to update stop");
    }
  };

  const handleActionClick = (actionId, rowIndex, actionObj) => {
    const formattedData = formatDataForTable(filteredStopsData || []);
    const rowData = formattedData[rowIndex];

    if (!rowData) {
      console.error("Row data is undefined for index:", rowIndex);
      return;
    }

    const stopId = rowData.id;
    const stopData = rowData.originalData;

    if (!stopId || !stopData) {
      console.error("Missing stopId or stopData in row", rowData);
      return;
    }

    const action = customActions.find((a) => a.id === actionId);
    if (!action) {
      console.error("Action not found:", actionId);
      return;
    }

    if (action.requiredPermission) {
      const { path, action: requiredAction } = action.requiredPermission;
      if (!checkPermission(path, requiredAction)) {
        console.log(`No permission for action: ${actionId}`);
        return;
      }
    }

    switch (actionId) {
      case "update":
        handleUpdateStop(stopId);
        break;
      case "view":
        handleViewStopDetails(stopData);
        break;
      case "delete":
        openDeleteModal(stopId, stopData);
        break;
      default:
        break;
    }
  };

  // Get counts for cards
  const activeStopsCount =
    stopsData.filter((stop) => stop.status === "ACTIVE").length || 0;
  const totalRoutesCount = new Set(
    stopsData.flatMap((stop) =>
      stop.routes ? stop.routes.map((route) => route.id) : []
    )
  ).size;

  // Show add button only if user has permission
  const showAddButton = checkPermission("/api/stop", "POST");
  const showTrackButton = checkPermission("/api/stop/organization", "GET");
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;
  return (
    <div style={{ minHeight: "100vh" }}>
      <SubTitle
        title="Stops"
        path={`${orgName || "organization"} > Stops`}
        add={showAddButton ? "createStop" : null}
        addPath="/stop/add"
        track={showTrackButton ? "Monitor Stops" : null}
        trackPath="/stops/all/map"
        onAddClick={handleAddStop}
        onTrackClick={handleViewAllStopsOnMap}
      />

      <Cards
        cardData={[
          {
            title: "totalStops",
            value: stopsData?.length || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "connectedRoutes",
            value: totalRoutesCount,
            backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
        ]}
      />

      <div className="table_container">
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <Suspense fallback={<div className="loader"></div>}>
          <DynamicTable
            headers={dynamicHeaders}
            data={formatDataForTable(filteredStopsData || [])}
            onActionClick={handleActionClick}
            actions={getPermittedActions()}
            loading={isLoading}
          />
        </Suspense>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDelete}
        title="Delete Stop"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {selectedStopData?.name || t("this stop")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />
    </div>
  );
}
